import { Abs, Center } from "@pdfking/math"
import { CreateMixin, CreateRenderFunction } from "@pdfking/reactivity/preact"
import type { PlotRect as PlotRectAction, PlotText as PlotTextAction } from "@pdfking/renderer-api"
import { PlotFunction } from "@pdfking/renderer-api"
import { Constant } from "@pdfking/tfunc"

import { PlotMixin as mixin } from "../mixins/PlotMixin"
import { PlotRect } from "./PlotRect"

const halign_to_anchor: Record<string, string> = {
  center: "middle",
  left: "start",
  right: "end",
}
const valign_to_baseline: Record<string, string> = {
  bottom: "auto",
  center: "middle",
  top: "hanging",
}
const halign_processor: Record<string, (w: number) => number> = {
  center: Constant(0),
  left: (w: number) => -Abs(w) / 2,
  right: (w: number) => Abs(w) / 2,
}
const valign_processor: Record<string, (h: number) => number> = {
  bottom: (h: number) => Abs(h) / 2,
  center: Constant(0),
  top: (h: number) => -Abs(h) / 2,
}

export const PlotTextMixin = CreateMixin({
  computed: {
    dominant_baseline() {
      return valign_to_baseline[this.action.vertical_align]
    },
    text() {
      return this.action.text
    },
    text_anchor() {
      return halign_to_anchor[this.action.horizontal_align]
    },
    text_transform() {
      const box = this.get_measures(this.action.position)
      const { height, width } = box
      const middle = Center(box)

      // Alignment is measured on the box as written: the shape carries no
      // rotation, and a turned label is a group around it. The flip is undone
      // about the centre so glyphs read the right way up, which also means the
      // vertical shift is applied in the un-flipped (y-down) frame.
      const h_shift = halign_processor[this.action.horizontal_align](width)
      const v_shift = valign_processor[this.action.vertical_align](height)
      return `${this.unflip(middle.x, middle.y)} translate(${h_shift} ${v_shift})`
    },
  },
  mixins: [mixin] as const,
  props: {
    action: {} as PlotTextAction,
  },
})

export const PlotText = CreateRenderFunction({
  mixins: [PlotTextMixin] as const,
  render() {
    return (
      <>
        <PlotRect
          action={{ ...this.action, enum: PlotFunction.PLOT_RECT } as PlotRectAction}
          context={this.context}
        />
        <text
          className="pointer-events-none"
          dominant-baseline={this.dominant_baseline}
          fill={this.get_color(this.action.text_color)}
          font-size={this.action.font_size}
          text-anchor={this.text_anchor}
          transform={this.text_transform}
        >
          {this.text}
        </text>
      </>
    )
  },
})
